import { useCallback, useEffect, useState } from "react";
import { useConversation } from "@elevenlabs/react";
import { fetchConversationToken } from "../api/elevenlabs";
import { useT } from "../i18n";
import { ELEVENLABS_AGENT_ID } from "./elevenlabsConfig";

function MicIcon() {
  return (
    <svg className="voice-assistant__fab-icon" viewBox="0 0 24 24" aria-hidden>
      <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v5a3 3 0 0 0 3 3Zm5-3a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V19H9a1 1 0 1 0 0 2h6a1 1 0 1 0 0-2h-2v-1.08A7 7 0 0 0 17 11Z" />
    </svg>
  );
}

type VoiceAssistantFabProps = {
  /** Extra bottom offset when match dock is visible */
  matchDock?: boolean;
};

export function VoiceAssistantFab({ matchDock = false }: VoiceAssistantFabProps) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const conversation = useConversation({
    onError: (message) => {
      setSessionError(typeof message === "string" ? message : t("voiceAssistant.errorGeneric"));
      setBusy(false);
    },
    onConnect: () => {
      setSessionError(null);
      setBusy(false);
    },
    onDisconnect: () => {
      setBusy(false);
    },
  });

  const { status, mode, isMuted, setMuted } = conversation;
  const isLive = status === "connected" || status === "connecting";
  const isSpeaking = status === "connected" && mode === "speaking";

  const statusLabel = (() => {
    if (status === "connecting") return t("voiceAssistant.statusConnecting");
    if (status === "connected" && mode === "speaking") return t("voiceAssistant.statusSpeaking");
    if (status === "connected" && mode === "listening") return t("voiceAssistant.statusListening");
    if (status === "error") return t("voiceAssistant.statusError");
    return t("voiceAssistant.statusIdle");
  })();

  const probeConfig = useCallback(async () => {
    const res = await fetchConversationToken(ELEVENLABS_AGENT_ID);
    if (!res.ok) {
      setConfigError(
        res.configured ? res.error : t("voiceAssistant.unavailableNotConfigured")
      );
      return false;
    }
    setConfigError(null);
    return res.token;
  }, [t]);

  useEffect(() => {
    if (!expanded) return;
    void probeConfig();
  }, [expanded, probeConfig]);

  const startVoice = async () => {
    setSessionError(null);
    setBusy(true);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setSessionError(t("voiceAssistant.micDenied"));
      setBusy(false);
      return;
    }

    const tokenOrFalse = await probeConfig();
    if (tokenOrFalse === false) {
      setBusy(false);
      return;
    }

    try {
      conversation.startSession({
        conversationToken: tokenOrFalse,
        connectionType: "webrtc",
      });
    } catch (err) {
      setSessionError(err instanceof Error ? err.message : t("voiceAssistant.errorGeneric"));
      setBusy(false);
    }
  };

  const stopVoice = () => {
    conversation.endSession();
    setBusy(false);
  };

  const toggleFab = () => {
    setExpanded((open) => !open);
  };

  const rootClass = matchDock ? "voice-assistant voice-assistant--match" : "voice-assistant";

  return (
    <div className={rootClass} data-testid="voice-assistant">
      {expanded ? (
        <div className="voice-assistant__panel" role="region" aria-label={t("voiceAssistant.panelAria")}>
          <p className="voice-assistant__title">{t("voiceAssistant.title")}</p>
          <p className={`voice-assistant__status${isLive ? " is-live" : ""}`} role="status">
            {statusLabel}
          </p>
          {configError ? (
            <p className="voice-assistant__error" role="alert">
              {configError}
            </p>
          ) : null}
          {sessionError ? (
            <p className="voice-assistant__error" role="alert">
              {sessionError}
            </p>
          ) : null}
          <div className="voice-assistant__actions">
            {status === "connected" || status === "connecting" ? (
              <button
                type="button"
                className="voice-assistant__btn voice-assistant__btn--stop"
                onClick={stopVoice}
                disabled={status === "connecting"}
                aria-label={t("voiceAssistant.stopAria")}
              >
                {t("voiceAssistant.stop")}
              </button>
            ) : (
              <button
                type="button"
                className="voice-assistant__btn voice-assistant__btn--primary"
                onClick={() => void startVoice()}
                disabled={busy || Boolean(configError)}
                aria-label={t("voiceAssistant.startAria")}
              >
                {busy ? t("voiceAssistant.starting") : t("voiceAssistant.start")}
              </button>
            )}
            {status === "connected" ? (
              <button
                type="button"
                className="voice-assistant__btn"
                onClick={() => setMuted(!isMuted)}
                aria-pressed={isMuted}
                aria-label={isMuted ? t("voiceAssistant.unmuteAria") : t("voiceAssistant.muteAria")}
              >
                {isMuted ? t("voiceAssistant.unmute") : t("voiceAssistant.mute")}
              </button>
            ) : null}
            <button
              type="button"
              className="voice-assistant__btn"
              onClick={() => setExpanded(false)}
              aria-label={t("voiceAssistant.closeAria")}
            >
              {t("voiceAssistant.close")}
            </button>
          </div>
        </div>
      ) : null}

      <div className="voice-assistant__fab-wrap">
        {isSpeaking ? <span className="voice-assistant__pulse" aria-hidden /> : null}
        <button
          type="button"
          className={`voice-assistant__fab${expanded || isLive ? " is-active" : ""}${isSpeaking ? " is-speaking" : ""}`}
          onClick={toggleFab}
          aria-expanded={expanded}
          aria-label={t("voiceAssistant.fabAria")}
        >
          <MicIcon />
        </button>
      </div>
    </div>
  );
}
